import Services from 'utils/services';
import {
  REQUEST_ADD_REACTION, SUCCESS_ADD_REACTION, FAIL_ADD_REACTION,
  REQUEST_REMOVE_REACTION, SUCCESS_REMOVE_REACTION, FAIL_REMOVE_REACTION,
  ADD_CONTENT_REACTION,
  REQUEST_REACTION_USERS, FAIL_RECEIVE_REACTION_USERS,
  RECEIVE_REACTION_USERS, FINISHED_REACTION_USERS,
  REQUEST_TAG_USERS, FAIL_RECEIVE_TAG_USERS,
  RECEIVE_TAG_USERS, FINISHED_TAG_USERS,
  REQUEST_FOLLOW_USER, FAIL_FOLLOW_USER, SUCCESS_FOLLOW_USER,
  REQUEST_UNFOLLOW_USER, FAIL_UNFOLLOW_USER, SUCCESS_UNFOLLOW_USER,
  SET_USER_FOLLOW,
  REQUEST_CURRENT_USER_DETAILS, FAIL_RECEIVE_CURRENT_USER_DETAILS, RECEIVE_CURRENT_USER_DETAILS,
  SET_REACTION_SHOULD_ANIMATE_IN, SET_REACTION_SHOULD_ANIMATE_OUT,
} from './actionTypes';
import {
  shouldCheckForFollowingTeamUpdates,
} from './featuredAthleteActionCreators';
import { generateObjectWithPascalCaseKeys } from 'utils/logging';

// Helpers
function getReactionStatus(state, communityContentType, communityContentRelatedId, reaction) {
  const { contentReactions, pendingContentReactions } = state.reaction;
  const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;

  const { currentUserReactions = [] } = contentReactions[communityContentIdKey];
  const pendingReactions = pendingContentReactions[communityContentIdKey] || [];

  const hasAlreadyReacted = currentUserReactions.includes(reaction);
  const hasPendingReaction = pendingReactions.includes(reaction);

  return {hasAlreadyReacted, hasPendingReaction};
}

function getFollowStatus(state, feedUserType, feedUserRelatedId) {
  const { userFollows = {}, pendingUserFollows = [] } = state.follow;
  const feedUserIdKey = `${feedUserType}-${feedUserRelatedId}`;
  const isFollowing = userFollows[feedUserIdKey] || false;
  const isPending = pendingUserFollows.includes(feedUserIdKey);

  return {isFollowing, isPending};
}

// Follow user
function requestFollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: REQUEST_FOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function failFollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: FAIL_FOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function successFollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: SUCCESS_FOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function followUser(feedUserType, feedUserRelatedId, { logData = {} }) {
  return dispatch => {
    dispatch(requestFollowUser(feedUserType, feedUserRelatedId));

    const requestLogData = Object.assign({}, logData, {
      func: 'Follow',
      op: 'FeedFriendship',
    });

    const requestData = {
      friendId: {
        type: feedUserType,
        relatedId: feedUserRelatedId,
      },
      logData: generateObjectWithPascalCaseKeys(requestLogData),
    };

    Services.followUser(requestData)
      .fail(() => dispatch(failFollowUser(feedUserType, feedUserRelatedId)))
      .done(() => {
        dispatch(successFollowUser(feedUserType, feedUserRelatedId));
        dispatch(shouldCheckForFollowingTeamUpdates(true));
      });
  };
}

// Unfollow user
function requestUnfollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: REQUEST_UNFOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function getPreviousReactionCount(state, communityContentType, communityContentRelatedId, reaction) {
  const { contentReactions } = state.reaction;
  const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;

  const { reactions = {} } = contentReactions[communityContentIdKey];
  const { count = 0 } = reactions[reaction] || {};

  return count;
}

// Manage reaction animations
export function setReactionShouldAnimateIn(communityContentType, communityContentRelatedId, reaction, shouldAnimateIn) {
  return {
    type: SET_REACTION_SHOULD_ANIMATE_IN,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
      shouldAnimateIn,
    },
  };
}

function failUnfollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: FAIL_UNFOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function successUnfollowUser(feedUserType, feedUserRelatedId) {
  return {
    type: SUCCESS_UNFOLLOW_USER,
    payload: {
      feedUserType,
      feedUserRelatedId,
    },
  };
}

function unfollowUser(feedUserType, feedUserRelatedId, { logData = {} }) {
  return dispatch => {
    dispatch(requestUnfollowUser(feedUserType, feedUserRelatedId));

    const requestLogData = Object.assign({}, logData, {
      func: 'Unfollow',
      op: 'FeedFriendship',
    });

    const requestData = {
      friendId: {
        type: feedUserType,
        relatedId: feedUserRelatedId,
      },
      logData: generateObjectWithPascalCaseKeys(requestLogData),
    };

    Services.unfollowUser(requestData)
      .fail(() => dispatch(failUnfollowUser(feedUserType, feedUserRelatedId)))
      .done(() => {
        dispatch(successUnfollowUser(feedUserType, feedUserRelatedId));
        dispatch(shouldCheckForFollowingTeamUpdates(true));
      });
  };
}

// Toggle follow user
export function toggleFollowUserIfNeeded(feedUserType, feedUserRelatedId, data) {
  return (dispatch, getState) => {
    const {
      isFollowing,
      isPending,
    } = getFollowStatus(getState(), feedUserType, feedUserRelatedId);

    if (!isPending) {
      if (data.searchPosition && data.logData) {
        const interaction = isFollowing ? 'Unfollow' : 'Follow';
        const feedUserId = {type: feedUserType, relatedId: feedUserRelatedId};
        Services.logCommunitySearchFeedUsersInteraction(
          interaction, feedUserId, data.logData.experiment,
          data.logData.requestId, data.searchPosition, isFollowing);
      }

      if (isFollowing) {
        return dispatch(unfollowUser(feedUserType, feedUserRelatedId, data));
      }
      return dispatch(followUser(feedUserType, feedUserRelatedId, data));
    }
  };
}

// Set user follow
export function setUserFollow(feedUserType, feedUserRelatedId, isFollowing) {
  return {
    type: SET_USER_FOLLOW,
    payload: {
      feedUserType,
      feedUserRelatedId,
      isFollowing,
    },
  };
}

export function setReactionShouldAnimateOut(communityContentType, communityContentRelatedId,
    reaction, shouldAnimateOut) {
  return {
    type: SET_REACTION_SHOULD_ANIMATE_OUT,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
      shouldAnimateOut,
    },
  };
}

// Add reaction
function requestAddReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: REQUEST_ADD_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function failAddReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: FAIL_ADD_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function successAddReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: SUCCESS_ADD_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function addReaction(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    const previousState = getState();

    dispatch(requestAddReaction(communityContentType, communityContentRelatedId, reaction));

    const previousReactionCount = getPreviousReactionCount(previousState, communityContentType,
                                                           communityContentRelatedId, reaction);
    if (previousReactionCount >= 0) {
      dispatch(setReactionShouldAnimateIn(communityContentType, communityContentRelatedId, reaction, true));
    }

    Services.addReaction(communityContentType, communityContentRelatedId, reaction, data)
      .fail(() => dispatch(failAddReaction(communityContentType, communityContentRelatedId, reaction)))
      .done(() => dispatch(successAddReaction(communityContentType, communityContentRelatedId, reaction)));
  };
}

function shouldAddReaction(state, communityContentType, communityContentRelatedId, reaction) {
  const { hasAlreadyReacted, hasPendingReaction } =
    getReactionStatus(state, communityContentType, communityContentRelatedId, reaction);
  return !(hasAlreadyReacted || hasPendingReaction);
}

export function addReactionIfNeeded(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    if (shouldAddReaction(getState(), communityContentType, communityContentRelatedId, reaction)) {
      return dispatch(addReaction(communityContentType, communityContentRelatedId, reaction, data));
    }
  };
}

// Remove reaction
function requestRemoveReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: REQUEST_REMOVE_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function failRemoveReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: FAIL_REMOVE_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function successRemoveReaction(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: SUCCESS_REMOVE_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function removeReaction(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch) => {
    dispatch(requestRemoveReaction(communityContentType, communityContentRelatedId, reaction));
    dispatch(setReactionShouldAnimateOut(communityContentType, communityContentRelatedId, reaction, true));

    Services.removeReaction(communityContentType, communityContentRelatedId, reaction, data)
      .then(() => dispatch(successRemoveReaction(communityContentType, communityContentRelatedId, reaction)))
      .fail(() => dispatch(failRemoveReaction(communityContentType, communityContentRelatedId, reaction)));
  };
}

function shouldRemoveReaction(state, communityContentType, communityContentRelatedId, reaction) {
  const { hasAlreadyReacted, hasPendingReaction } =
    getReactionStatus(state, communityContentType, communityContentRelatedId, reaction);
  return (hasAlreadyReacted && !hasPendingReaction);
}

export function removeReactionIfNeeded(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    if (shouldRemoveReaction(getState(), communityContentType, communityContentRelatedId, reaction)) {
      return dispatch(removeReaction(communityContentType, communityContentRelatedId, reaction, data));
    }
  };
}

// Toggle reaction
export function toggleReactionIfNeeded(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    const { hasAlreadyReacted, hasPendingReaction } =
      getReactionStatus(getState(), communityContentType, communityContentRelatedId, reaction);
    if (!hasPendingReaction) {
      if (hasAlreadyReacted) {
        return dispatch(removeReaction(communityContentType, communityContentRelatedId, reaction, data));
      }
      return dispatch(addReaction(communityContentType, communityContentRelatedId, reaction, data));
    }
  };
}

// Add contentReaction (for bootstrapping the store)
function addContentReaction(communityContentType, communityContentRelatedId, contentReaction) {
  return {
    type: ADD_CONTENT_REACTION,
    payload: {
      communityContentType,
      communityContentRelatedId,
      contentReaction,
    },
  };
}

export function addContentReactionIfNeeded(communityContentType, communityContentRelatedId, contentReaction) {
  return (dispatch, getState) => {
    const state = getState();
    const { contentReactions } = state.reaction;
    const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;
    const hasContentReaction = contentReactions.hasOwnProperty(communityContentIdKey);

    if (!hasContentReaction) {
      const { reactions } = contentReaction;

      let existingReactions = [];
      if (Object.keys(reactions).length > 0) {
        existingReactions = Object.keys(reactions).sort((reactionA, reactionB) => (
          (reactions[reactionB].count || 0) - (reactions[reactionA].count || 0)
        ));
      }

      contentReaction.existingReactions = existingReactions;

      return dispatch(addContentReaction(communityContentType, communityContentRelatedId, contentReaction));
    }
  };
}

// Fetch reaction users for displaying reaction details
function requestReactionUsers(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: REQUEST_REACTION_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function failReceiveReactionUsers(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: FAIL_RECEIVE_REACTION_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function receiveReactionUsers(communityContentType, communityContentRelatedId, reaction, responseData) {
  const { firstReactionDate, users } = responseData;

  return {
    type: RECEIVE_REACTION_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
      firstReactionDate,
      users,
    },
  };
}

function finishedReactionUsers(communityContentType, communityContentRelatedId, reaction) {
  return {
    type: FINISHED_REACTION_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
      reaction,
    },
  };
}

function fetchReactionUsers(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    const state = getState();
    const { contentReactionDetails } = state.reaction;

    const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;

    const { reactionDetails = {} } = contentReactionDetails[communityContentIdKey] || {};
    const { nextReactionDate } = reactionDetails[reaction] || {};

    const requestData = Object.assign({}, data, {
      reaction,
      beforeDateTime: nextReactionDate,
      excludeCurrentUser: true,
    });

    dispatch(requestReactionUsers(communityContentType, communityContentRelatedId, reaction));

    Services.getInteractions(communityContentType, communityContentRelatedId, requestData)
      .fail(() => dispatch(failReceiveReactionUsers(communityContentType, communityContentRelatedId, reaction)))
      .done(responseData => {
        const action = dispatch(receiveReactionUsers(communityContentType, communityContentRelatedId,
                                                     reaction, responseData));
        const { users = [] } = action.payload;
        // If the next state has the same number of users as the previous state
        // the store needs to be updated to indicate that no more users should be loaded
        if (!users.length) {
          dispatch(finishedReactionUsers(communityContentType, communityContentRelatedId, reaction));
        }

        // Update the follow status for each user to ensure we have the latest state
        users.forEach(({ feedUserId, isFollowing }) => {
          const { type: feedUserType, relatedId: feedUserRelatedId } = feedUserId;
          dispatch(setUserFollow(feedUserType, feedUserRelatedId, isFollowing));
        });
      });
  };
}

function shouldFetchReactionUsers(state, communityContentType, communityContentRelatedId, reaction) {
  const { contentReactionDetails } = state.reaction;
  const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;
  const { reactionDetails = {} } = contentReactionDetails[communityContentIdKey] || {};
  const {
    isError = false,
    isFetching = false,
    isFinished = false,
  } = reactionDetails[reaction] || {};

  return !(isFetching || isFinished || isError);
}

export function fetchReactionUsersIfNeeded(communityContentType, communityContentRelatedId, reaction, data) {
  return (dispatch, getState) => {
    if (shouldFetchReactionUsers(getState(), communityContentType, communityContentRelatedId, reaction)) {
      return dispatch(fetchReactionUsers(communityContentType, communityContentRelatedId, reaction, data));
    }
  };
}

// Fetch tag users for displaying tag details
function requestTagUsers(communityContentType, communityContentRelatedId) {
  return {
    type: REQUEST_TAG_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
    },
  };
}

function failReceiveTagUsers(communityContentType, communityContentRelatedId) {
  return {
    type: FAIL_RECEIVE_TAG_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
    },
  };
}

function receiveTagUsers(communityContentType, communityContentRelatedId, responseData) {
  const { lastUserTagId = null, users = [] } = responseData;

  return {
    type: RECEIVE_TAG_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
      lastUserTagId,
      users,
    },
  };
}

function finishedTagUsers(communityContentType, communityContentRelatedId) {
  return {
    type: FINISHED_TAG_USERS,
    payload: {
      communityContentType,
      communityContentRelatedId,
    },
  };
}

function fetchTagUsers(communityContentType, communityContentRelatedId, data) {
  return (dispatch, getState) => {
    const state = getState();
    const { contentTagDetails } = state.tags;

    const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;

    const { tagDetails = {} } = contentTagDetails[communityContentIdKey] || {};
    const { lastUserTagId } = tagDetails || {};

    const requestData = Object.assign({}, data);
    if (lastUserTagId) {
      requestData.lastUserTagId = lastUserTagId;
    }

    dispatch(requestTagUsers(communityContentType, communityContentRelatedId));

    Services.getTaggedUsers(communityContentType, communityContentRelatedId, requestData)
      .fail(() => dispatch(failReceiveTagUsers(communityContentType, communityContentRelatedId)))
      .done(responseData => {
        const { users = [] } = responseData;
        if (!users.length) {
          // If the next state has the same number of users as the previous state
          // the store needs to be updated to indicate that no more users should be loaded
          dispatch(finishedTagUsers(communityContentType, communityContentRelatedId));
        }

        dispatch(receiveTagUsers(communityContentType, communityContentRelatedId, responseData));

        // Update the follow status for each user to ensure we have the latest state
        users.forEach(({ feedUserId, isFollowing }) => {
          const { type: feedUserType, relatedId: feedUserRelatedId } = feedUserId;
          dispatch(setUserFollow(feedUserType, feedUserRelatedId, isFollowing));
        });
      });
  };
}

function shouldFetchTagUsers(state, communityContentType, communityContentRelatedId) {
  const { contentTagDetails } = state.tags;
  const communityContentIdKey = `${communityContentType}-${communityContentRelatedId}`;
  const { tagDetails = {} } = contentTagDetails[communityContentIdKey] || {};
  const {
    isError = false,
    isFetching = false,
    isFinished = false,
  } = tagDetails || {};

  return !(isFetching || isFinished || isError);
}

export function fetchTagUsersIfNeeded(communityContentType, communityContentRelatedId, data) {
  return (dispatch, getState) => {
    if (shouldFetchTagUsers(getState(), communityContentType, communityContentRelatedId)) {
      return dispatch(fetchTagUsers(communityContentType, communityContentRelatedId, data));
    }
  };
}

// Fetch current user details
function requestCurrentUserDetails() {
  return {
    type: REQUEST_CURRENT_USER_DETAILS,
    payload: {},
  };
}

function failReceiveCurrentUserDetails() {
  return {
    type: FAIL_RECEIVE_CURRENT_USER_DETAILS,
    payload: {},
  };
}

function receiveCurrentUserDetails(responseData) {
  return {
    type: RECEIVE_CURRENT_USER_DETAILS,
    payload: {
      currentUserDetails: responseData,
    },
  };
}

function fetchCurrentUserDetails() {
  return dispatch => {
    dispatch(requestCurrentUserDetails());
    Services.getCurrentUserDetails()
      .fail(() => dispatch(failReceiveCurrentUserDetails()))
      .done(responseData => dispatch(receiveCurrentUserDetails(responseData)));
  };
}

function shouldFetchCurrentUserDetails(state) {
  const { userDetails } = state;
  const { currentUser } = userDetails;
  const {
    isError = false,
    isFetching = false,
    details: currentUserDetails,
  } = currentUser;

  // Only fetch current user details if the store doesn't already contain it
  const hasCurrentUserDetails = !!currentUserDetails;

  return !(isError || isFetching) && !hasCurrentUserDetails;
}

export function fetchCurrentUserDetailsIfNeeded() {
  return (dispatch, getState) => {
    if (shouldFetchCurrentUserDetails(getState())) {
      return dispatch(fetchCurrentUserDetails());
    }
  };
}
